import {Hotkey, NumberField} from '@wandb/components';
import React, {useCallback, useState} from 'react';

import {useDebouncedValue} from '../../../../../util/useDebouncedValue';
import {
  useMediaVideoPlayerTimeStepDisplay,
  useShouldShowMediaVideoPlayerTimeStepResetAtLevel,
} from '../../hooks';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';

export const DEFAULT_VIDEO_PLAYER_TIME_STEP = 0.1;
const FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  unit: 'second',
  style: 'unit',
  unitDisplay: 'narrow',
};

export const VideoPlayerTimeStep = () => {
  const {updateConfig, unsetConfigField, settingsLevel, panelRef} =
    useMediaSettingsContext();
  const videoPlayerTimeStep = useMediaVideoPlayerTimeStepDisplay(
    settingsLevel,
    panelRef
  );
  const isSetAtLevel = useShouldShowMediaVideoPlayerTimeStepResetAtLevel(
    settingsLevel,
    panelRef
  );
  const updateVideoPlayerTimeStep = useCallback(
    (newValue?: number) => {
      const parsedValue = newValue ?? DEFAULT_VIDEO_PLAYER_TIME_STEP;
      if (parsedValue === DEFAULT_VIDEO_PLAYER_TIME_STEP) {
        // avoid storing default values in the config
        unsetConfigField('videoPlayerTimeStep');
      } else {
        updateConfig({
          videoPlayerTimeStep: parsedValue,
        });
      }
    },
    [updateConfig, unsetConfigField]
  );

  const {value: debouncedValue, debouncedSetValue} = useDebouncedValue(
    videoPlayerTimeStep,
    500,
    updateVideoPlayerTimeStep
  );

  // Local state to control the value of the input so we don't have to wait for the updateConfig call to propagate
  const [value, setValue] = useState(debouncedValue);
  const onChange = useCallback(
    (newValue?: number) => {
      setValue(newValue);
      debouncedSetValue(newValue);
    },
    [debouncedSetValue]
  );

  return (
    <div
      className="flex items-center justify-between"
      data-test="step-stride-length-input">
      <SettingTitle
        title="Video seek step"
        configField="videoPlayerTimeStep"
        isDirty={isSetAtLevel}
        unsetConfigField={() => unsetConfigField('videoPlayerTimeStep')}
        tooltip={
          <div className="flex gap-2">
            Increment of time to seek the video when using the arrow keys.
            <Hotkey keys={['ArrowLeft']} name="Seek backward" />
            <Hotkey keys={['ArrowRight']} name="Seek forward" />
          </div>
        }
      />
      <NumberField
        defaultValue={DEFAULT_VIDEO_PLAYER_TIME_STEP}
        minValue={0}
        // 30fps = 1/30 = 0.03333333333333333 seconds per frame, so setting stride length to be on that order of magnitude if users want to approximate frame by frame playback
        step={0.001}
        value={value}
        onChange={onChange}
        formatOptions={FORMAT_OPTIONS}
      />
    </div>
  );
};
